import { FEATURE_FLAGS } from "@/data/globals";
import { ListState } from "@/data/redux/listSlice";
import { GameSystems, IFleetShipSelection, ISelectedUnit } from "@/data/types";
import { isFeatureEnabled } from "@/env";
import { groupBy, sumBy } from "lodash-es";
import { GameSystemService } from "../GameSystemService";
import { isHero } from "../Helpers";
import UnitService from "../UnitService";
import FtlService from "./FtlService";

const V3_6 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

type ForceOrgThresholds = {
  units: number;
  duplicateUnits: number;
  heroes: number;
  models?: number;
  tough?: { per: number; amt: number };
};

export const maxUnitPercentage = 0.35; // 35% of total points
export const getMaxAllowedSingleUnit = (points: number) => Math.floor((points * maxUnitPercentage) / 5) * 5;

const thresholds: Record<number, ForceOrgThresholds> = V3_6
  ? {
      [GameSystems.gf]: {
        heroes: 500,
        duplicateUnits: 1000,
        units: 200,
        tough: { per: 20, amt: 1 }
      },
      [GameSystems.gff]: {
        heroes: 400,
        duplicateUnits: 400,
        units: 80,
        tough: { per: 40, amt: 3 }
      },
      [GameSystems.aof]: {
        heroes: 375,
        duplicateUnits: 750,
        units: 150,
        tough: { per: 15, amt: 1 }
      },
      [GameSystems.aofs]: {
        heroes: 300,
        duplicateUnits: 300,
        units: 60,
        tough: { per: 30, amt: 3 }
      },
      [GameSystems.aofr]: null // Copied from AoF
    }
  : {
      [GameSystems.gf]: {
        units: 200,
        duplicateUnits: 1000,
        heroes: 500,
        models: 0
      },
      [GameSystems.gff]: {
        units: 30,
        duplicateUnits: 150,
        heroes: 150,
        models: 20
      },
      [GameSystems.aof]: {
        units: 150,
        duplicateUnits: 750,
        heroes: 375,
        models: 0
      },
      [GameSystems.aofs]: {
        units: 25,
        duplicateUnits: 125,
        heroes: 125,
        models: 15
      },
      [GameSystems.aofr]: null // Copied from AoF
    };

thresholds[GameSystems.aofr] = thresholds[GameSystems.aof];

thresholds[GameSystems.ftl] = {
  heroes: 200,
  duplicateUnits: 150,
  units: 50,
  models: 0
};

export default class ForceOrgService {
  public static getLimits(
    gameSystemId: GameSystems,
    list: { pointsLimit?: number },
    currentPointTotal: number
  ) {
    const settings = thresholds[gameSystemId];
    const points = list.pointsLimit || currentPointTotal;
    const heroes = Math.floor(points / settings.heroes);
    const maxTough = settings.tough ? Math.floor(points / settings.tough.per) * settings.tough.amt : 0;

    const result = {
      heroes: heroes,
      casters: heroes, // Caster limit is same as heroes in v3.0
      units: Math.floor(points / settings.units),
      duplicateUnits: 1 + Math.floor(points / settings.duplicateUnits),
      models: Math.floor(points / settings.models || 1),
      maxTough
    };

    return result;
  }

  public static getErrors(
    gameSystemId: GameSystems,
    list: { pointsLimit?: number },
    units: ISelectedUnit[],
    currentPointTotal: number
  ): string[] {
    if (!list) return [];
    if (GameSystemService.isQuestAI({ id: gameSystemId })) return []; // No Force org for Quest
    const errors = [];

    const settings = thresholds[gameSystemId];

    if (list.pointsLimit > 0 && currentPointTotal > list.pointsLimit)
      errors.push(`Points limit exceeded: ${currentPointTotal}/${list.pointsLimit}`);

    const points = list.pointsLimit || currentPointTotal;
    const maxAllowedSingleUnit = getMaxAllowedSingleUnit(points);
    const limits = this.getLimits(gameSystemId, list, currentPointTotal);

    const unitCount = units.filter((u) => !u.joinToUnit).length;
    const heroes = units.filter((u) => isHero(u));
    const heroCount = heroes.length;
    const joinedHeroes = heroes.filter(
      (u) => u.joinToUnit && units.some((t) => t.selectionId === u.joinToUnit)
    );
    const joinedIds = joinedHeroes.map((u) => u.joinToUnit);
    const nonCombinedUnitsGrouped = groupBy(
      units.filter((u) => !(u.combined && !u.joinToUnit)),
      (u) => u.id
    );
    const unitsOverDuplicateLimit = Object.keys(nonCombinedUnitsGrouped)
      .map((key) => ({
        unitName: units.find((u) => u.id === key).name,
        count: nonCombinedUnitsGrouped[key].length
      }))
      .filter((grp) => grp.count > limits.duplicateUnits);

    const fullUnits = UnitService.getFullUnitList(units, false);

    const isBattleSystem = [GameSystems.gf, GameSystems.aof, GameSystems.aofr].includes(gameSystemId);

    //#region All Game Systems

    if (heroCount > limits.heroes) errors.push(`Max 1 Hero per full ${settings.heroes}pts.`);

    if (unitCount > limits.units) {
      const combinedMsg = isBattleSystem ? ` (combined units count as just 1 unit)` : "";
      errors.push(`Max 1 unit per full ${settings.units}pts${combinedMsg}.`);
    }

    if (unitsOverDuplicateLimit.length > 0)
      errors.push(
        `Cannot have more than ${limits.duplicateUnits} copies of a particular unit (${unitsOverDuplicateLimit
          .map((x) => x.unitName)
          .join(", ")}).`
      ); // combined units still count as one

    if (fullUnits.some((u) => u.unitPointsAll > maxAllowedSingleUnit))
      errors.push(
        `May not bring any single unit worth more than 35% of total points (${maxAllowedSingleUnit.toFixed(
          0
        )}pts).`
      );

    //#endregion

    /*
    all games - "Players may not bring any single unit worth more than 35% of their total points."
    skirmish games - "Players must configure their lists so that they only have a max. of 1 model in play per full 20pts in their list."
    for battle games: "Example: For a 2000pts game, you can take max. 4 heroes, max. 3 copies of each unit, no unit worth over 660pts, and max. 10 units in total."
    for skirmish games: "Example: For a 300pts game, you can take max. 2 heroes, max. 3 copies of each unit, no unit worth over 99pts, max. 10 units in total, and max. 15 models."
     */

    if (isBattleSystem) {
      if (units.some((u) => u.combined && u.size === 1))
        errors.push(`Cannot combine units of unit size [1].`);

      if (units.some((u) => u.size === 1 && joinedIds.includes(u.selectionId)))
        errors.push(`Heroes cannot join units that only contain a single model.`);

      if (new Set(joinedIds).size < joinedIds.length)
        errors.push(`A unit can only have a maximum of one Hero attached.`);
    }

    if (settings.models > 0) {
      const modelCount = sumBy(units, (u) => u.size);
      if (modelCount > limits.models)
        errors.push(
          `Max 1 model per full ${
            settings.models
          }pts. Maximum valid number of models is ${limits.models}, current total is ${modelCount}.`
        );
    }
    if (settings.tough) {
      const armyTough = sumBy(units, (x) => x.size * UnitService.getTough(x));
      if (armyTough > limits.maxTough)
        errors.push(
          `Max ${isBattleSystem ? "Models/" : ""}Tough is ${limits.maxTough}, current total is ${armyTough}.`
        );
    }

    if (gameSystemId === GameSystems.aofs) {
      // Rule: When preparing your army you may only have one model with one of the following upgrades (across the entire army).
      const sergeants = units.filter((u) =>
        u.selectedUpgrades.some((upgrade) => upgrade.option.label === "Sergeant")
      );
      const sergeantsCount = sergeants.length;
      const musicians = units.filter((u) =>
        u.selectedUpgrades.some((upgrade) => upgrade.option.label === "Musician")
      );
      const musiciansCount = musicians.length;
      const banners = units.filter((u) =>
        u.selectedUpgrades.some((upgrade) => upgrade.option.label === "Banner")
      );
      const bannersCount = banners.length;

      if (sergeantsCount + musiciansCount + bannersCount > 1) {
        errors.push(
          `Max 1 of the following upgrades per army (not one of each!): Sergeant, Musician or Banner.`
        );
      }
    }

    // if (loadedArmyBooks.length > 2) {
    //   errors.push("Players may bring units from up to two factions in the same list.");
    // }

    //const unitsByArmy = groupBy(units, (x) => x.armyId);
    // const pointsByArmy = Object.keys(unitsByArmy).map((key) =>
    //   unitsByArmy[key].reduce((pts, unit) => pts + calculateUnitTotal(unit), 0)
    // );

    // if (
    //   currentPointTotal > 0 &&
    //   loadedArmyBooks.length > 1 &&
    //   (points / list.pointsLimit) * 100 >= 40 &&
    //   !pointsByArmy.some((x) => (x / points) * 100 >= 60)
    // ) {
    //   errors.push(
    //     "Mixed armies must consist of at least 60% worth of units from their primary faction."
    //   );
    // }

    // Check unique
    const unitsById = Object.values(groupBy(units, (x) => x.id));
    for (const group of unitsById.filter((x) => x.length > 1)) {
      if (group[0].rules.find((x) => x.name === "Unique")) {
        errors.push(`${group[0].name} is a Unique unit and may only be taken once per army.`);
      }
    }

    return errors;
  }

  public static getFtlLimits(list: ListState, currentPointTotal: number) {
    const settings = thresholds[GameSystems.ftl];
    const points = list.pointsLimit || currentPointTotal;
    const heroes = Math.floor(points / settings.heroes);
    const squadronPerPts = 35;

    return {
      heroes: heroes,
      ships: Math.floor(points / settings.units),
      duplicates: Math.floor(points / settings.duplicateUnits),
      squadrons: Math.floor(points / squadronPerPts)
    };
  }

  public static getFtlErrors(
    list: ListState,
    allShips: IFleetShipSelection[],
    currentPointTotal: number
  ): string[] {
    const errors = [];
    const settings = thresholds[GameSystems.ftl];
    const ships = allShips.filter((x) => !x.squadron);
    const squadrons = allShips.filter((x) => x.squadron);

    const limits = this.getFtlLimits(list, currentPointTotal);
    const heroes = ships.filter((x) => Boolean(x.hero));
    const groupedShips = groupBy(ships, (x) => x.name);
    const squadronCount = sumBy(squadrons, (x) => x.count);

    if (list.pointsLimit > 0 && currentPointTotal > list.pointsLimit)
      errors.push(`Points limit exceeded: ${currentPointTotal}/${list.pointsLimit}`);

    for (const ship of ships) {
      const filledSlots = FtlService.getOccupiedSlotCount(ship);
      if (filledSlots < ship.slots) {
        errors.push(`${ship.name} only has ${filledSlots} slots filled out of ${ship.slots}.`);
      }
    }

    // If heroes > limit
    if (heroes.length > limits.heroes) {
      errors.push(`Players may only bring up to 1 hero per ${settings.heroes}pts in their fleet`);
    }

    // If any groupedShips > duplicate limit
    for (const key in groupedShips) {
      const group = groupedShips[key];
      if (group.length > limits.duplicates) {
        errors.push(
          `Players may only bring X copies of the same ship, where X is 1 per ${settings.duplicateUnits}pts in their fleet`
        );
      }
    }

    // If max ships > limit
    if (ships.length > limits.ships) {
      errors.push(`Fleets may only have max. of 1 ship per ${settings.units}pts.`);
    }

    // If squadrons > limit
    if (squadronCount > limits.squadrons) {
      errors.push(
        `Fleets may only have max. as many squadrons as total upgrades across all ships ${limits.squadrons}.`
      );
    }

    return errors;
  }
}
